import storefront from "../global/storefront";
import { SHOWROOM_STYLES_QUERY } from "../global/storefront/queries";
import { escapeHtml, toNumericId } from "./showroom-api";

// The showroom JSON stores only ids, so [data-showroom-style] hooks are filled
// here; results are cached so re-hydrating after a refresh costs no requests.

const MAX_IDS_PER_REQUEST = 250;
const cache = new Map();

const variantGid = (id) => (String(id).startsWith("gid://") ? String(id) : `gid://shopify/ProductVariant/${id}`);
const productGid = (id) => (String(id).startsWith("gid://") ? String(id) : `gid://shopify/Product/${id}`);

function styleKey(el) {
  const { variantId, productId } = el.dataset;
  if (variantId) return variantGid(variantId);
  if (productId) return productGid(productId);
  return "";
}

// Prefers the clean "showroom__" lineup image (matching the color when there
// are several), then the variant image, then the product's featured image.
function pickImage(product, variant, colorLabel) {
  const colorHandle = colorLabel ? window.handleize(colorLabel) : "";
  const showroomImages = (product?.images?.nodes || []).filter((image) => image.url.includes("showroom__"));
  const colorMatch = colorHandle && showroomImages.find((image) => image.url.toLowerCase().includes(colorHandle));

  return colorMatch || showroomImages[0] || variant?.image || product?.featuredImage || null;
}

function normalize(node) {
  if (!node) return null;

  const isVariant = !!node.product;
  const product = isVariant ? node.product : node;
  const variant = isVariant ? node : product.variants?.nodes?.[0];
  if (!product) return null;

  const pattern = product.colorPattern?.references?.nodes?.[0];
  const colorLabel = pattern?.swatchLabel?.value || variant?.title?.split(" - ").pop() || "";
  const variantId = toNumericId(variant?.id);
  const baseUrl = product.onlineStoreUrl || (product.handle ? window.productUrl(product.handle) : "");

  return {
    productId: toNumericId(product.id),
    variantId,
    title: (product.title || "").split("|")[0].trim(),
    url: baseUrl && variantId ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}variant=${variantId}` : baseUrl,
    price: variant?.price ? window.formatMoney(Number(variant.price.amount), false, variant.price.currencyCode) : "",
    image: pickImage(product, variant, colorLabel),
    colorLabel,
    colorImage: pattern?.swatchImage?.reference?.image?.url || "",
    hasProductGroup: !!product.productGroup?.reference?.id,
    productGroupId: product.productGroup?.reference?.id || "",
  };
}

async function fetchStyles(keys) {
  const missing = keys.filter((key) => !cache.has(key));

  for (let i = 0; i < missing.length; i += MAX_IDS_PER_REQUEST) {
    const ids = missing.slice(i, i + MAX_IDS_PER_REQUEST);
    const response = await storefront.request({ query: SHOWROOM_STYLES_QUERY, variables: { ids } });
    const nodes = response?.data?.nodes || [];

    ids.forEach((id, index) => cache.set(id, normalize(nodes[index])));
  }
}

function renderStyle(el, style) {
  el.classList.remove("is-style-loading");

  if (!style) {
    el.classList.add("is-style-missing");
    return;
  }

  // Keep ids complete for actions that need them (e.g. a product-only item).
  if (!el.dataset.productId) el.dataset.productId = style.productId;
  if (!el.dataset.variantId && style.variantId) el.dataset.variantId = style.variantId;

  el.dataset.productTitle = style.title;
  if (style.productGroupId) el.dataset.productGroupId = style.productGroupId;
  el.dataset.colorLabel = style.colorLabel;
  if (style.colorImage) el.dataset.colorImage = style.colorImage;

  el.querySelectorAll("[data-style-image]").forEach((slot) => {
    if (!style.image || slot.querySelector("img")) return;

    const img = document.createElement("img");
    img.src = style.image.url;
    img.alt = style.image.altText || style.title;
    img.loading = "lazy";
    img.crossOrigin = "anonymous"; // lets the PDF export draw it onto a canvas
    if (slot.dataset.styleImageClass) img.className = slot.dataset.styleImageClass;
    slot.append(img);
  });

  el.querySelectorAll("[data-style-title]").forEach((node) => (node.textContent = style.title));
  el.querySelectorAll("[data-style-price]").forEach((node) => (node.textContent = style.price));
  el.querySelectorAll("[data-style-link]").forEach((link) => style.url && (link.href = style.url));
  el.querySelectorAll("[data-style-color]").forEach((node) => {
    node.innerHTML = `
      ${style.colorImage ? `<img class="showroom-swatch__image" src="${escapeHtml(style.colorImage)}" alt="" loading="lazy">` : ""}
      <span class="showroom-swatch__label">${escapeHtml(style.colorLabel)}</span>
    `;
  });

  if (style.hasProductGroup) {
    el.querySelectorAll("[data-closet-colors-open]").forEach((button) => (button.hidden = false));
  }

  el.dataset.styleHydrated = "";
}

export async function hydrateShowroomStyles(root = document) {
  const elements = [...root.querySelectorAll("[data-showroom-style]:not([data-style-hydrated])")];
  const keyed = elements.map((el) => [el, styleKey(el)]);
  const keys = [...new Set(keyed.map(([, key]) => key).filter(Boolean))];

  keyed.forEach(([el, key]) => {
    if (key) el.classList.add("is-style-loading");
    else el.classList.add("is-style-missing");
  });

  if (!keys.length) return;

  try {
    await fetchStyles(keys);
  } catch (error) {
    console.error("Showroom styles could not be loaded.", error);
  }

  keyed.forEach(([el, key]) => key && renderStyle(el, cache.get(key)));
  document.dispatchEvent(new CustomEvent("showroom:styles-hydrated"));
}

// Points an already-shown style (a card, a lineup slide) at another product
// and variant — e.g. after "Change" color — and fills it in again: its
// image, title, price, color and links, plus the ids on its buttons.
export function restyleShowroomStyle(el, { productId, variantId }) {
  if (!el) return Promise.resolve();

  const ids = { productId: String(productId || ""), variantId: String(variantId || "") };
  [el, ...el.querySelectorAll("[data-variant-id], [data-product-id]")].forEach((node) => {
    if ("productId" in node.dataset) node.dataset.productId = ids.productId;
    if ("variantId" in node.dataset) node.dataset.variantId = ids.variantId;
    if ("storedVariantId" in node.dataset) node.dataset.storedVariantId = ids.variantId;
  });
  el.querySelectorAll("[data-style-image] img").forEach((img) => img.remove());
  delete el.dataset.styleHydrated;

  return hydrateShowroomStyles(el.parentElement || document);
}

const init = () => hydrateShowroomStyles();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

document.addEventListener("showroom:refreshed", () => hydrateShowroomStyles());
